import {
  useState,
  CSSProperties,
  FC,
  MouseEvent,
  PropsWithChildren,
} from 'react'
import useMedia from 'use-media'

import { breakpoints } from 'styles/media'

import MoreButton from './display/MoreButton'

const ShowMore: FC<
  PropsWithChildren<{
    style?: CSSProperties
  }>
> = ({ children, style }) => {
  const [expanded, setExpanded] = useState(false)

  const handleClick = (e: MouseEvent) => {
    e.preventDefault()
    setExpanded(!expanded)
  }

  return (
    <div>
      {expanded && children}
      <MoreButton onClick={handleClick} style={style}>
        <span>{expanded ? '-' : '+'}&nbsp;</span>
        <span>Show {expanded ? 'Less' : 'More'}</span>
      </MoreButton>
    </div>
  )
}

type ShowMoreAtBreakpointProps = {
  breakpoint: keyof typeof breakpoints
  equality: 'less_than' | 'greater_than'
  style?: CSSProperties
}

export const ShowMoreAtBreakpoint: FC<
  PropsWithChildren<ShowMoreAtBreakpointProps>
> = ({ breakpoint, equality, children, style }) => {
  const mediaQuery = (() => {
    switch (equality) {
      case 'greater_than':
        return { minWidth: breakpoints[breakpoint] }
      case 'less_than':
        return { maxWidth: breakpoints[breakpoint] }
      default:
        return null
    }
  })()

  // Pass `false` as defaultState so first client render matches SSR (see MediaQueryContext).
  // Hook is called unconditionally with a no-op fallback query when equality is unknown
  // to satisfy react-hooks/rules-of-hooks; the gate below ignores the result in that case.
  const mediaMatches = useMedia(mediaQuery ?? { minWidth: '0px' }, false)
  const shouldWrapInShowMore = !!mediaQuery && mediaMatches

  if (shouldWrapInShowMore) {
    return <ShowMore style={style}>{children}</ShowMore>
  }

  return <>{children}</>
}

export default ShowMore
